import { useCallback } from 'react';
import { useUserAccessRightsQuery } from 'src/services/user';

/**
 * Shared "can the current user reach this route" check, mirroring the access
 * gating in NavItemList: `dashboard` is always reachable, every other route
 * needs a matching access right with read (or read_write). Feature flags
 * surface as the absence of an access right, so a route hidden behind an
 * inactive flag returns false here — used to keep the command palette from
 * finding pages/entities the user cannot actually open.
 *
 * `link` is the bare route without a leading slash (e.g. `cases/claims`), as in
 * routes.ts; access rights are keyed by `/${link}`.
 */
export const useRouteAccess = () => {
  const { data: accessRights, isLoading } = useUserAccessRightsQuery();

  const hasAccess = useCallback(
    (link?: string): boolean => {
      if (!link) return false;
      if (link.startsWith('dashboard')) return true;
      if (isLoading || !accessRights) return false;
      const matchedRight = accessRights.find(right => right.route === `/${link}`);
      return Boolean(matchedRight?.read || matchedRight?.read_write);
    },
    [accessRights, isLoading],
  );

  /**
   * Substring variant for entities whose access right is not a nav route — e.g.
   * the Settings sub-tabs (`manage_users`), which Settings.tsx itself gates with
   * `right.route.includes(tab)`. Mirror that so the palette stays in sync.
   */
  const hasAccessIncludes = useCallback(
    (token?: string): boolean => {
      if (!token || isLoading || !accessRights) return false;
      return accessRights.some(right => right.route.includes(token) && (right.read || right.read_write));
    },
    [accessRights, isLoading],
  );

  return { hasAccess, hasAccessIncludes, isLoading };
};
